import React from "react";
import { connect } from "react-redux";
import { formatMessage } from "lib/globalize-util";
import { Table, TableHead, TableBody, TableRow, TableCell, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowDropDown, ArrowDropUp, InfoOutlined } from "@material-ui/icons";
import type { IStore } from "reducers";
import type { CategoryInsights } from "models/insights/category-insights";
import theme from "styles/theme";
import { startCase, toLower } from "lodash";
import { InlineLoading } from "components/content-loading";
import { DATATEST } from "constants/integration";

const useStyles = makeStyles({
    container: {
        fontSize: "1.3rem",
        color: theme.palette.textColor,
    },
    title: {
        fontWeight: "bold",
        marginBottom: "20px",
    },
    description: {
        paddingBottom: "5px",
    },
    headerRow: {
        "& .MuiTableCell-head": {
            fontWeight: "bold",
            paddingBottom: 24,
        },
    },
    categoryCell: {
        /*
         * This for truncated the text if it taking more than 100px width
         */
        maxWidth: "100px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    cell: {
        fontSize: "1.3rem",
        paddingLeft: 4,
        paddingRight: 4,
    },
    yearTitle: {
        position: "relative",
        whiteSpace: "nowrap",
    },
    yearTitleInfoIcon: {
        position: "absolute",
        top: 19,
        right: 2,
    },
    arrow: {
        verticalAlign: "middle",
    },
    positivePercentage: {
        color: theme.palette.positive,
    },
    negativePercentage: {
        color: theme.palette.errorColor2,
    },
    tooltip: {
        fontSize: "1.1rem",
    },
});

interface StateProps {
    categoryInsights: CategoryInsights[];
    isLoading: boolean;
}

const getPercentageClass = (classes: ReturnType<typeof useStyles>, percentageChange: number): string => {
    if (percentageChange > 0) {
        return classes.positivePercentage;
    } else if (percentageChange < 0) {
        return classes.negativePercentage;
    } else {
        return "";
    }
};

const formatPercentage = (value: number): string => {
    if (typeof value === "number") {
        const roundedValue = Math.round(value);
        if (value > 0 && value < 1) {
            return "<1%";
        } else if (value < 0 && value > -1) {
            return "<1%";
        }
        return `${roundedValue}%`;
    } else {
        return "-";
    }
};

const OrderInsightsCategories: React.FC<StateProps> = ({ categoryInsights, isLoading }) => {
    const classes = useStyles();

    if (isLoading) {
        return <InlineLoading />;
    }

    return (
        <div className={classes.container}>
            <div className={classes.title}>{formatMessage("Top ordered categories")}</div>
            {categoryInsights?.length > 0 && (
                <div className={classes.description}>
                    {formatMessage("Data is based on all USD orders for this brand.")}
                </div>
            )}
            {categoryInsights?.length > 0 ? (
                <Table>
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell className={classes.cell}>{formatMessage("Category")}</TableCell>
                            <TableCell className={classes.cell} align="right">
                                <div className={classes.yearTitle}>
                                    {formatMessage("Last year")}
                                    <Tooltip
                                        classes={{ tooltip: classes.tooltip }}
                                        title={formatMessage("13-24 months ago")}
                                    >
                                        <InfoOutlined
                                            fontSize="small"
                                            className={classes.yearTitleInfoIcon}
                                            data-test={DATATEST.INSIGHTS.CATEGORIES.LAST_YEAR_INFO_ICON}
                                        />
                                    </Tooltip>
                                </div>
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                                <div className={classes.yearTitle}>
                                    {formatMessage("This year")}
                                    <Tooltip
                                        classes={{ tooltip: classes.tooltip }}
                                        title={formatMessage("Within the last 12 months")}
                                    >
                                        <InfoOutlined
                                            fontSize="small"
                                            className={classes.yearTitleInfoIcon}
                                            data-test={DATATEST.INSIGHTS.CATEGORIES.THIS_YEAR_INFO_ICON}
                                        />
                                    </Tooltip>
                                </div>
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                                Change
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categoryInsights.map((category) => (
                            <TableRow
                                key={category.name}
                                data-test={`${DATATEST.INSIGHTS.CATEGORIES.ROW}-${toLower(category.name)}`}
                            >
                                <TableCell
                                    className={`${classes.cell} ${classes.categoryCell}`}
                                    component="th"
                                    scope="row"
                                >
                                    <Tooltip
                                        classes={{ tooltip: classes.tooltip }}
                                        title={category.name ? startCase(toLower(category.name)) : ""}
                                        placement="bottom-start"
                                        interactive // the tooltip will not close when the mouse is over it
                                    >
                                        <span data-test={DATATEST.INSIGHTS.CATEGORIES.NAME}>
                                            {startCase(toLower(category.name))}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell
                                    className={classes.cell}
                                    align="right"
                                    data-test={DATATEST.INSIGHTS.CATEGORIES.PERCENTAGE.LAST_YEAR}
                                >
                                    {formatPercentage(category.percentage.lastYear)}
                                </TableCell>
                                <TableCell
                                    className={classes.cell}
                                    align="right"
                                    data-test={DATATEST.INSIGHTS.CATEGORIES.PERCENTAGE.THIS_YEAR}
                                >
                                    {formatPercentage(category.percentage.thisYear)}
                                </TableCell>
                                <TableCell
                                    className={`${classes.cell} ${getPercentageClass(classes, category.percentage.yearlyChange)}`}
                                    align="right"
                                    data-test={DATATEST.INSIGHTS.CATEGORIES.PERCENTAGE.YEARLY_CHANGE}
                                >
                                    {typeof category.percentage.yearlyChange === "number" &&
                                        category.percentage.yearlyChange !== 0 &&
                                        (category.percentage.yearlyChange > 0 ? (
                                            <ArrowDropUp
                                                fontSize="large"
                                                className={classes.arrow}
                                                data-test={DATATEST.INSIGHTS.CATEGORIES.ARROW_DROP_UP}
                                            />
                                        ) : (
                                            <ArrowDropDown
                                                fontSize="large"
                                                className={classes.arrow}
                                                data-test={DATATEST.INSIGHTS.CATEGORIES.ARROW_DROP_DOWN}
                                            />
                                        ))}
                                    {formatPercentage(category.percentage.yearlyChange)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <div> {formatMessage("No Category trend data available.")} </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: IStore) => {
    return {
        scope: state.scope,
        categoryInsights: state.insights.categories,
        isLoading: state.insights.isLoading.categories,
    };
};

export const CategoryInsightsContainer = connect<StateProps, {}, {}>(mapStateToProps)(OrderInsightsCategories);
export default CategoryInsightsContainer;
